/**
 * Save n Make — the client half of the library.
 *
 * Types mirror the API exactly rather than being restated loosely, because the
 * distinctions the server is careful about — provenance, confidence, whether a
 * capture actually produced anything — are the distinctions the UI has to show.
 * A looser type here would let the interface quietly claim more than the server
 * said.
 */
import { del, get, patch, post } from './api'

export type Provenance = 'source' | 'user' | 'suggested'
export type Confidence = 'certain' | 'ambiguous' | 'missing'
export type Status = 'want_to_try' | 'tested' | 'cafe_approved'

export const STATUS_LABEL: Record<Status, string> = {
  want_to_try: 'Want to try',
  tested: 'Tested',
  cafe_approved: 'Cafe approved',
}

export interface Ingredient {
  id: string; position: number
  quantity: number | null; unit: string | null
  item: string; note: string | null; raw: string | null
  provenance: Provenance; confidence: Confidence
}

export interface Step {
  id: string; position: number; text: string
  temperature: number | null; temperatureUnit: 'F' | 'C' | null
  durationSeconds: number | null
  actionKind: 'oven' | 'stovetop' | 'rest' | 'prep' | 'chill' | null
  cue: string | null; ingredientRefs: string[]
  provenance: Provenance; confidence: Confidence
}

export interface Equipment { id: string; name: string; provenance: Provenance }

export interface Flag {
  id: string; field: string; kind: 'missing' | 'ambiguous'
  detail: string; stepPosition: number | null; resolvedAt: number | null
}

export interface CategoryRef { id: string; kind: string; name: string; slug: string; source: 'suggested' | 'user' }

export interface RecipeRow {
  id: string; title: string; description: string | null
  sourceUrl: string | null; sourcePlatform: string | null
  sourceCreator: string | null; sourceCreatorUrl: string | null; sourceThumbUrl: string | null
  yieldText: string | null; servings: number | null
  totalMinutes: number | null; activeMinutes: number | null
  status: Status; favorite: boolean; isSample: boolean
  priorMakes: number | null; revision: number
  createdAt: number; updatedAt: number
  timesCooked: number; lastCookedOn: string | null; openFlags: number
  categories: CategoryRef[]
  lastSessionAt: number | null; plannedFor: string | null
  checkedInAt: number | null; nudgesOff: boolean
}

export interface Recipe extends RecipeRow {
  ingredients: Ingredient[]; steps: Step[]; equipment: Equipment[]; flags: Flag[]
}

export interface Category { id: string; kind: string; name: string; slug: string; isSeed: boolean; count: number }

export interface Collection {
  id: string; name: string; description: string | null; cap: number | null; position: number
  items: { recipeId: string; rank: number; title: string; favorite: boolean; status: Status }[]
}

export interface Timer {
  id: string; name: string; deadlineAt: number; durationSeconds: number
  status: 'running' | 'rang' | 'stopped'; stepPosition: number | null
}

export interface Session {
  id: string; recipeId: string
  status: 'active' | 'completed' | 'abandoned'
  currentStep: number; confirmations: Record<string, string[]>
  snapshot: Recipe; recipeRevision: number
  startedAt: number; completedAt: number | null
  timers: Timer[]; stale: boolean
}

export interface Readiness {
  ready: boolean
  ingredients: Ingredient[]; equipment: Equipment[]; attention: Flag[]
  blocking: { stepPosition: number; text: string; missing: 'temperature' | 'duration' }[]
}

export interface Run {
  id: string; recipeId: string; title: string; sessionId: string | null
  cookedOn: string; servingsActual: number | null
  notes: string | null; adjustments: string | null
  source: 'session' | 'manual'; recipeRevision: number | null
  recipeChangedSince: boolean; tags: string[]; createdAt: number
}

export interface Gap {
  missing: 'ingredients' | 'steps' | 'both'
  found: { ingredients: number; steps: number }
  reason: string
  todo: string
}

export interface CaptureResult {
  id: string
  status: 'pending' | 'parsed' | 'insufficient' | 'unreachable' | 'duplicate'
  reason: string | null
  platform: string | null
  url: string | null
  recipeId: string | null
  existingRecipeId?: string
  parsed: { cleanedText?: string; ingredients: unknown[]; steps: unknown[]; usable: boolean } | null
  gap?: Gap | null
  savedPartial?: boolean
}

export interface PlatformInfo { label: string; fetchable: boolean; note: string }

export const CATEGORY_KINDS: { key: string; label: string }[] = [
  { key: 'area', label: 'Cafe area' },
  { key: 'dish', label: 'Dish type' },
  { key: 'cuisine', label: 'Cuisine' },
  { key: 'ingredient', label: 'Main ingredient' },
  { key: 'time', label: 'Time' },
  { key: 'effort', label: 'Effort' },
  { key: 'tag', label: 'Tag' },
]

/* ── calls ──────────────────────────────────────────────────────────────── */

export const bootstrap = () => post<{ ok: true; seeded: number }>('/api/library/bootstrap')

export const listRecipes = () => get<{ recipes: RecipeRow[] }>('/api/library/recipes').then((r) => r.recipes)
export const getRecipe = (id: string) => get<{ recipe: Recipe }>(`/api/library/recipes/${id}`).then((r) => r.recipe)
export const createRecipe = (input: Partial<RecipeRow>) => post<{ id: string; duplicate: boolean }>('/api/library/recipes', input)
export const updateRecipe = (id: string, input: Record<string, unknown>) =>
  patch<{ recipe: Recipe }>(`/api/library/recipes/${id}`, input).then((r) => r.recipe)
export const deleteRecipe = (id: string) => del(`/api/library/recipes/${id}`)

export const saveIngredients = (id: string, ingredients: Partial<Ingredient>[]) =>
  fetchPut(`/api/library/recipes/${id}/ingredients`, { ingredients })
export const saveSteps = (id: string, steps: Partial<Step>[]) =>
  fetchPut(`/api/library/recipes/${id}/steps`, { steps })
export const saveEquipment = (id: string, equipment: { name: string }[]) =>
  fetchPut(`/api/library/recipes/${id}/equipment`, { equipment })

/** PUT is not in the shared client, and a whole-list replace is genuinely a PUT. */
async function fetchPut(path: string, body: unknown): Promise<Recipe> {
  const res = await fetch(path, {
    method: 'PUT', credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error((await res.json().catch(() => ({})) as { error?: string }).error ?? 'Save failed.')
  return (await res.json() as { recipe: Recipe }).recipe
}

export const addCategoryToRecipe = (recipeId: string, body: { categoryId?: string; kind?: string; name?: string }) =>
  post<{ recipe: Recipe }>(`/api/library/recipes/${recipeId}/categories`, body).then((r) => r.recipe)
export const removeCategoryFromRecipe = (recipeId: string, categoryId: string) =>
  del<{ recipe: Recipe }>(`/api/library/recipes/${recipeId}/categories/${categoryId}`).then((r) => r.recipe)
export const resolveFlag = (recipeId: string, flagId: string) =>
  post<{ recipe: Recipe }>(`/api/library/recipes/${recipeId}/flags/${flagId}`).then((r) => r.recipe)

export const listCategories = () => get<{ categories: Category[] }>('/api/library/categories').then((r) => r.categories)
export const createCategory = (kind: string, name: string) =>
  post<{ category: Category }>('/api/library/categories', { kind, name }).then((r) => r.category)
export const renameCategory = (id: string, name: string) =>
  patch<{ categories: Category[] }>(`/api/library/categories/${id}`, { name }).then((r) => r.categories)
export const deleteCategory = (id: string) =>
  del<{ categories: Category[] }>(`/api/library/categories/${id}`).then((r) => r.categories)

export const listCollections = () => get<{ collections: Collection[] }>('/api/library/collections').then((r) => r.collections)
export const createCollection = (name: string, description?: string, cap?: number | null) =>
  post<{ id: string }>('/api/library/collections', { name, description, cap })
export const addToCollection = (collectionId: string, recipeId: string) =>
  post<{ collections: Collection[] }>(`/api/library/collections/${collectionId}/items`, { recipeId }).then((r) => r.collections)
export const removeFromCollection = (collectionId: string, recipeId: string) =>
  del<{ collections: Collection[] }>(`/api/library/collections/${collectionId}/items/${recipeId}`).then((r) => r.collections)
export const deleteCollection = (id: string) =>
  del<{ collections: Collection[] }>(`/api/library/collections/${id}`).then((r) => r.collections)

export interface DiscoveryResponse {
  recipes: RecipeRow[]
  total: number
  excluded: { cooked: number; byFilters: number }
}

export const discover = (params: URLSearchParams) =>
  get<DiscoveryResponse>(`/api/library/discovery?${params.toString()}`)

export const capturePlatforms = () => get<{ platforms: Record<string, PlatformInfo> }>('/api/capture/platforms').then((r) => r.platforms)
export const captureLink = (url: string) => post<CaptureResult>('/api/capture/link', { url })
export const captureText = (text: string, url?: string, title?: string, allowPartial?: boolean) =>
  post<CaptureResult>('/api/capture/text', { text, url, title, allowPartial })

/** One picture on a capture, as the server sees it. */
export interface CaptureImage {
  position: number
  /** False when the model could not transcribe this one. `reason` says why. */
  read: boolean
  model: string | null
  reason: string | null
  url: string
}

export interface ScreenshotRead {
  id: string
  /** Where this picture sits in the run of them. */
  position: number
  /** THIS picture's transcription, not the whole capture's. */
  transcription: string | null
  transcriptionModel: string | null
  reason: string | null
  needsOwnerAction: boolean
  urlsFound: string[]
  /** Every picture on the capture so far, in order. */
  images: CaptureImage[]
}

/**
 * Upload one screenshot and have it read.
 *
 * `captureId` appends this picture to a capture already started, which is how a
 * caption spread over three screenshots becomes one recipe. One picture per
 * request, deliberately: each read is a metered model call that can fail on its
 * own, and sending all three together would mean one failure losing the two
 * that worked — and a single request long enough for the browser to give up on.
 */
export async function captureScreenshot(file: File, url?: string, captureId?: string): Promise<ScreenshotRead> {
  const form = new FormData()
  form.append('file', file)
  if (url) form.append('url', url)
  if (captureId) form.append('captureId', captureId)
  const res = await fetch('/api/capture/screenshot', { method: 'POST', credentials: 'same-origin', body: form })
  if (!res.ok) throw new Error((await res.json().catch(() => ({})) as { error?: string }).error ?? 'Upload failed.')
  return res.json() as Promise<ScreenshotRead>
}

/** Matches MAX_CAPTURE_IMAGES on the server, which is where it is enforced. */
export const MAX_CAPTURE_IMAGES = 8

export const readiness = (recipeId: string) =>
  get<{ readiness: Readiness; recipe: Recipe }>(`/api/cooking/readiness/${recipeId}`)
export const activeSessions = () => get<{ sessions: Session[] }>('/api/cooking/active').then((r) => r.sessions)
export const beginCooking = (recipeId: string) =>
  post<{ session: Session; resumed: boolean }>('/api/cooking/sessions', { recipeId })
export const getSession = (id: string) => get<{ session: Session }>(`/api/cooking/sessions/${id}`).then((r) => r.session)
export const saveProgress = (id: string, body: { currentStep?: number; confirmations?: Record<string, string[]> }) =>
  patch<{ session: Session }>(`/api/cooking/sessions/${id}`, body).then((r) => r.session)
export const startTimer = (sessionId: string, name: string, seconds: number, stepPosition?: number) =>
  post<{ session: Session }>(`/api/cooking/sessions/${sessionId}/timers`, { name, seconds, stepPosition }).then((r) => r.session)
export const adjustTimer = (timerId: string, body: { addSeconds?: number; status?: Timer['status'] }) =>
  patch<{ session: Session }>(`/api/cooking/timers/${timerId}`, body).then((r) => r.session)
export const dropTimer = (timerId: string) =>
  del<{ session: Session }>(`/api/cooking/timers/${timerId}`).then((r) => r.session)
export const completeSession = (id: string, body: Record<string, unknown>) =>
  post<{ ok: true; runId: string; alreadyRecorded: boolean }>(`/api/cooking/sessions/${id}/complete`, body)
export const abandonSession = (id: string) => post(`/api/cooking/sessions/${id}/abandon`)
export const history = (recipeId?: string) =>
  get<{ runs: Run[] }>(`/api/cooking/history${recipeId ? `?recipe=${recipeId}` : ''}`).then((r) => r.runs)
/** Her answer to "still want to make this?" — the server enforces the dates. */
export const checkIn = (recipeId: string, body: { action: 'plan'; date: string } | { action: 'unplan' | 'later' | 'stop' | 'resume' }) =>
  post<{ ok: true }>(`/api/library/recipes/${recipeId}/checkin`, body)
export const recordPastMake = (body: Record<string, unknown>) => post<{ runId: string }>('/api/cooking/runs', body)

/* ── formatting ─────────────────────────────────────────────────────────── */

/** Fractions, because "0.33 cup" is not a measurement anybody owns a cup for. */
const FRACTIONS: [number, string][] = [
  [1 / 8, '⅛'], [1 / 4, '¼'], [1 / 3, '⅓'], [3 / 8, '⅜'], [1 / 2, '½'],
  [5 / 8, '⅝'], [2 / 3, '⅔'], [3 / 4, '¾'], [7 / 8, '⅞'],
]

export function formatQuantity(value: number | null): string {
  if (value === null) return ''
  const whole = Math.floor(value)
  const rest = value - whole
  if (rest < 0.02) return String(whole)
  const match = FRACTIONS.find(([f]) => Math.abs(rest - f) < 0.02)
  if (!match) return String(Math.round(value * 100) / 100)
  return whole ? `${whole}${match[1]}` : match[1]
}

const UNIT_SHORT: Record<string, string> = {
  tablespoon: 'tbsp', teaspoon: 'tsp', gram: 'g', kilogram: 'kg', ounce: 'oz',
  pound: 'lb', milliliter: 'ml', liter: 'l', cup: 'cup',
}

export function formatIngredient(i: Ingredient): string {
  const qty = formatQuantity(i.quantity)
  const unit = i.unit ? (UNIT_SHORT[i.unit] ?? i.unit) : ''
  const head = [qty, unit].filter(Boolean).join(' ')
  return [head, i.item].filter(Boolean).join(' ') + (i.note ? `, ${i.note}` : '')
}

export function formatDuration(seconds: number | null): string {
  if (!seconds) return ''
  const h = Math.floor(seconds / 3600)
  const m = Math.round((seconds % 3600) / 60)
  if (h && m) return `${h}h ${m}m`
  if (h) return `${h}h`
  return `${m}m`
}

/** mm:ss, or h:mm:ss past an hour. Never negative — a passed deadline is 0. */
export function clockFor(msRemaining: number): string {
  const total = Math.max(0, Math.round(msRemaining / 1000))
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return h ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}
